<template>
  <section class="relationship-refusal-strip" aria-labelledby="relationship-refusal-strip-title">
    <div class="relationship-refusal-strip__head">
      <v-icon small class="relationship-refusal-strip__icon">mdi-alert-circle-outline</v-icon>
      <span id="relationship-refusal-strip-title">{{ heading }}</span>
      <small>{{ hint }}</small>
    </div>
    <div class="relationship-refusal-strip__groups">
      <div v-for="group in summary.groups" :key="group.name" class="relationship-refusal-strip__group">
        <h4>{{ group.name }}</h4>
        <button
          v-for="entry in group.entries"
          :key="entry.key"
          type="button"
          class="relationship-refusal-strip__reason"
          :aria-pressed="activeKey === entry.key ? 'true' : 'false'"
          @click="$emit('filter', activeKey === entry.key ? null : entry.key)">
          <v-icon small>{{ entry.icon }}</v-icon>
          <span class="relationship-refusal-strip__title">{{ entry.title }}</span>
          <span class="relationship-refusal-strip__count">{{ entry.count }}</span>
        </button>
      </div>
    </div>
  </section>
</template>

<script>
// Refused rows grouped by reason above the run table (showcase C, "reasons" strip). Selecting a
// reason filters the table to those rows; selecting it again shows every row.
export default {
  name: 'RelationshipRefusalStrip',
  props: {
    summary: { type: Object, required: true },
    activeKey: { type: String, default: null },
    ownerKind: { type: String, default: null },
    partial: { type: Boolean, default: false },
  },
  computed: {
    heading() {
      const total = this.summary.total;
      const noun = this.ownerKind === 'facilityGroup' ? 'Facility Group' : 'Facility';
      return `${total} ${total === 1 ? 'row needs' : 'rows need'} a ${noun} fix`;
    },
    hint() {
      return this.partial
        ? 'Counts cover the loaded rows. Select a reason to show only those rows.'
        : 'Select a reason to show only those rows.';
    },
  },
};
</script>

<style lang="scss" scoped>
.relationship-refusal-strip {
  padding: var(--ds-space-sm) var(--ds-space-md);
  border: 1px solid var(--ds-border-subtle);
  border-radius: var(--ds-radius-md);
  background: var(--v-bg3-base);
}
.relationship-refusal-strip__head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--ds-space-xs) var(--ds-space-sm);
  font-size: var(--ds-font-body-sm);
  font-weight: 600;
}
.relationship-refusal-strip__icon { color: #a06d00 !important; }
.relationship-refusal-strip__head small {
  color: var(--run-muted-ink, #5c6b66);
  font-size: var(--ds-font-caption);
  font-weight: 400;
}
.relationship-refusal-strip__groups {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: var(--ds-space-sm) 20px;
  margin-top: var(--ds-space-sm);
}
.relationship-refusal-strip__group h4 {
  margin: 0 0 var(--ds-space-xs);
  color: var(--run-muted-ink, #5c6b66);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.relationship-refusal-strip__reason {
  display: flex;
  width: calc(100% + 16px);
  min-height: 32px;
  align-items: center;
  gap: var(--ds-space-sm);
  margin: 0 -8px;
  padding: var(--ds-space-xs) var(--ds-space-sm);
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--v-text-base);
  font-size: var(--ds-font-body-sm);
  text-align: left;
  cursor: pointer;
}
.relationship-refusal-strip__reason .v-icon { color: #a06d00 !important; }
.relationship-refusal-strip__reason:hover { background: var(--ds-control-hover); }
.relationship-refusal-strip__reason[aria-pressed='true'] { background: rgba(251, 189, 28, 0.16); font-weight: 600; }
.relationship-refusal-strip__reason:focus-visible {
  outline: var(--ds-focus-ring-width) solid var(--ds-focus-ring-color);
  outline-offset: var(--ds-focus-ring-offset);
}
.relationship-refusal-strip__title { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.relationship-refusal-strip__count {
  margin-left: auto;
  color: var(--run-muted-ink, #5c6b66);
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}
</style>
